import React from 'react';
import { Check, X, Cloud, Server, Users, BarChart3, Shield, Star, Award, Layers, Globe, Clock, Zap } from 'lucide-react';

const LMSPlatform: React.FC = () => {
  const serviceAreas = [
    {
      icon: <Users size={32} />,
      title: "Configuración y Personalización",
      description: "Adaptamos la plataforma a tu identidad institucional y modelo educativo. Temas visuales personalizados, estructura de categorías y roles de usuario definidos a medida.",
      benefit: "Una plataforma que refleja tu marca y se ajusta perfectamente a tus procesos académicos."
    },
    {
      icon: <Cloud size={32} />,
      title: "Hosting de Alto Rendimiento",
      description: "Infraestructura cloud optimizada para LMS (AWS/Google Cloud). Arquitectura escalable que soporta peaks de concurrencia durante exámenes o inicios de ciclo.",
      benefit: "Velocidad de carga superior y disponibilidad del 99.9%, sin caídas en momentos críticos."
    },
    {
      icon: <Shield size={32} />,
      title: "Seguridad y Respaldos",
      description: "Protocolos de seguridad avanzados, certificados SSL, firewall y backups automáticos diarios con retención histórica para proteger tus datos.",
      benefit: "Tranquilidad total sabiendo que la información académica y de estudiantes está protegida."
    },
    {
      icon: <Layers size={32} />,
      title: "Gestión de Actualizaciones",
      description: "Mantenemos tu LMS actualizado con las últimas versiones estables, parches de seguridad y mejoras de rendimiento sin interrumpir el servicio.",
      benefit: "Acceso siempre a las últimas funcionalidades sin complicaciones técnicas ni riesgos."
    },
    {
      icon: <Globe size={32} />,
      title: "Integración de Herramientas",
      description: "Conectamos tu LMS con sistemas de videoconferencia (Zoom, Teams), repositorios, bibliotecas y herramientas externas mediante LTI.",
      benefit: "Ecosistema de aprendizaje conectado y fluido para docentes y estudiantes."
    },
    {
      icon: <BarChart3 size={32} />,
      title: "Analítica y Reportería",
      description: "Configuración de reportes avanzados para seguimiento académico, retención de estudiantes y desempeño docente.",
      benefit: "Datos claros para tomar decisiones pedagógicas y administrativas informadas."
    }
  ];

  const plans = [
    {
      name: "Institucional",
      price: "$250.000",
      period: "/mes",
      description: "Para colegios e institutos medianos",
      popular: false,
      features: [
        { text: "Hasta 500 usuarios activos", included: true },
        { text: "Hosting optimizado compartido", included: true },
        { text: "Tema visual personalizado básico", included: true },
        { text: "Soporte técnico estándar (48h)", included: true },
        { text: "Backups diarios (retención 7 días)", included: true },
        { text: "Dominio personalizado", included: true },
        { text: "Integraciones avanzadas", included: false },
        { text: "SLA garantizado 99.9%", included: false },
        { text: "Ambiente de pruebas (Staging)", included: false }
      ],
      buttonText: "Comenzar Institucional",
      buttonClass: "btn-secondary"
    },
    {
      name: "Profesional",
      price: "$550.000",
      period: "/mes",
      description: "Para universidades y educación continua",
      popular: true,
      features: [
        { text: "Hasta 2.000 usuarios activos", included: true },
        { text: "Servidor VPS dedicado optimizado", included: true },
        { text: "Tema visual corporativo completo", included: true },
        { text: "Soporte prioritario (12h)", included: true },
        { text: "Backups diarios (retención 30 días)", included: true },
        { text: "Dominio personalizado + SSL EV", included: true },
        { text: "Integración Zoom/Teams/Webex", included: true },
        { text: "SLA garantizado 99.9%", included: true },
        { text: "Ambiente de pruebas (Staging)", included: false }
      ],
      buttonText: "Más Popular",
      buttonClass: "btn-primary"
    },
    {
      name: "Enterprise",
      price: "$1.200.000",
      period: "/mes o cotizar",
      description: "Para grandes instituciones",
      popular: false,
      features: [
        { text: "Usuarios ilimitados (Cluster)", included: true },
        { text: "Infraestructura Alta Disponibilidad", included: true },
        { text: "Diseño UX/UI totalmente a medida", included: true },
        { text: "Soporte dedicado 24/7", included: true },
        { text: "Política backups personalizada", included: true },
        { text: "Auditoría de seguridad anual", included: true },
        { text: "Integraciones API/Webservices ERP", included: true },
        { text: "SLA garantizado 99.95%", included: true },
        { text: "Ambiente de pruebas + Desarrollo", included: true }
      ],
      buttonText: "Cotizar Enterprise",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <Clock size={24} />,
      title: "Uptime Garantizado",
      description: "Infraestructura redundante para minimizar interrupciones"
    },
    {
      icon: <Zap size={24} />,
      title: "Velocidad Superior",
      description: "Optimización de caché y bases de datos para carga rápida"
    },
    {
      icon: <Shield size={24} />,
      title: "Seguridad Robusta",
      description: "Protección contra ataques DDoS y vulnerabilidades web"
    },
    {
      icon: <Users size={24} />,
      title: "Escalabilidad",
      description: "Crece de 100 a 100.000 usuarios sin cambiar de proveedor"
    }
  ];

  const useCases = [
    {
      title: "Universidades e Institutos",
      description: "Campus virtual centralizado para pregrado y postgrado, con integración a sistemas de matrícula y biblioteca.",
      results: "Experiencia unificada para miles de estudiantes"
    },
    {
      title: "Capacitación Corporativa",
      description: "Portal de formación para colaboradores con seguimiento de progreso, certificados automáticos y reportes a RRHH.",
      results: "Aumento en tasas de finalización de cursos"
    },
    {
        title: "Educación Continua",
        description: "Plataforma flexible para venta y dictado de cursos, diplomados y talleres abiertos al público.",
        results: "Monetización efectiva del conocimiento"
    }
  ];

  return (
    <section id="lms-platform" className="lms-platform-landing">
      {/* Hero Section */}
      <div className="crm-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <Award size={16} />
                Expertos en eLearning
              </div>
              <h1 className="hero-title">
                Plataformas LMS Administradas
                <span className="highlight">Moodle & Canvas</span>
              </h1>
              <p className="hero-description">
                Infraestructura cloud de alto rendimiento, configuración experta y soporte técnico especializado. 
                Olvídate de los problemas técnicos y enfócate en enseñar.
              </p>
              <div className="hero-cta">
                <a href="#contacto" className="btn btn-primary btn-lg">
                  Agendar Asesoría Técnica
                </a>
                <a href="#planes" className="btn btn-secondary btn-lg">
                  Ver Planes de Hosting
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-number">99.9%</div>
                  <div className="stat-label">Uptime Garantizado</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">+50k</div>
                  <div className="stat-label">Usuarios concurrentes</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">24/7</div>
                  <div className="stat-label">Monitoreo activo</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="container">
        <div className="service-areas">
          <div className="section-header">
            <h2>Gestión Integral de tu Campus Virtual</h2>
            <p className="section-subtitle">
              Nos encargamos de toda la capa técnica para que tu institución opere sin interrupciones
            </p>
          </div>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>Por qué elegir nuestro servicio administrado</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <div className="section-header">
            <h2>Soluciones adaptadas a tu sector</h2>
            <p className="section-subtitle">
              Experiencia comprobada en diversos entornos educativos y corporativos
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <h4>{useCase.title}</h4>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-results">
                  <Check size={20} className="check-icon" />
                  <span>{useCase.results}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planes" className="pricing-section">
          <div className="section-header">
            <h2>Planes de Implementación y Hosting</h2>
            <p className="section-subtitle">
              Soluciones escalables que crecen junto con tu institución
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-cross" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pricing-footer">
                  <a href="#contacto" className={`btn ${plan.buttonClass} btn-block`}>
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="pricing-note">
            <p>💡 ¿Ya tienes Moodle? Ofrecemos migración gratuita al contratar planes anuales.</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="final-cta">
          <div className="cta-content">
            <h3>¿Necesitas un LMS potente y confiable?</h3>
            <p>
              Conversa con nuestros arquitectos de soluciones para diseñar la infraestructura 
              educativa ideal para tu institución.
            </p>
            <div className="cta-buttons">
              <a href="#contacto" className="btn btn-primary btn-lg">
                Contactar Ahora
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LMSPlatform;
